/**
 * FacadeContextMenu Hook
 *
 * Pure HTML context menu with native positioning and events.
 * Right-click on the trigger opens a floating menu at the cursor position.
 * Items are server-rendered inside a hidden host element and cloned on open.
 *
 * Note on using a hidden <div> instead of <template>: LiveView's static change
 * tracking for function components inside a `:for` comprehension emits
 * `data-phx-skip` on static parts, relying on morphdom to merge dynamics into
 * existing DOM. morphdom cannot see into a <template>'s inert .content
 * DocumentFragment, so dynamically appended iterations end up with empty
 * statics (e.g., missing icons). A regular hidden element keeps statics in
 * the live DOM tree where morphdom can patch them.
 */

import { pushEvent, type ViewHook } from '../helpers/utils'

// ── Types ──────────────────────────────────────────────────────────────

interface ContextMenuHook extends ViewHook {
  panel: HTMLElement | null
  cleanup: (() => void) | null
  mounted(): void
  destroyed(): void
}

// ── Helpers ────────────────────────────────────────────────────────────

/** Focusable (non-disabled, non-divider) menu items inside a container. */
const getFocusableItems = (panel: HTMLElement): HTMLElement[] =>
  Array.from(
    panel.querySelectorAll<HTMLElement>(
      '[role="menuitem"]:not([aria-disabled="true"])'
    )
  )

/** Position the panel near (x, y), flipping when it would overflow the viewport. */
const positionPanel = (panel: HTMLElement, x: number, y: number): void => {
  // Render off-screen first so we can measure
  Object.assign(panel.style, {
    position: 'fixed',
    visibility: 'hidden',
    left: '0px',
    top: '0px'
  })

  const { width, height } = panel.getBoundingClientRect()
  const vw = window.innerWidth
  const vh = window.innerHeight

  const left = x + width > vw ? Math.max(0, x - width) : x
  const top = y + height > vh ? Math.max(0, y - height) : y

  Object.assign(panel.style, {
    visibility: '',
    left: `${left}px`,
    top: `${top}px`
  })
}

/** Read phx-* attributes from a menu item and push the event to LiveView. */
const sendItemEvent = (item: HTMLElement, hook: ViewHook): void => {
  const phxClick = item.getAttribute('phx-click')
  if (!phxClick) {
    return
  }

  const phxTarget = item.getAttribute('phx-target') ?? undefined

  const payload: Record<string, unknown> = {
    value: item.dataset.value ?? '',
    checked: item.dataset.checked === 'true'
  }

  const phxValue =
    item.getAttribute('phx-value') ?? item.getAttribute('phx-value-item')

  if (phxValue) {
    try {
      Object.assign(payload, JSON.parse(phxValue))
    } catch {
      payload.item = phxValue
    }
  }

  pushEvent(hook, phxClick, payload, phxTarget)
}

/** Move focus between menu items, wrapping at edges. */
const moveFocus = (panel: HTMLElement, direction: 1 | -1): void => {
  const items = getFocusableItems(panel)
  if (items.length === 0) {
    return
  }

  const current = items.indexOf(document.activeElement as HTMLElement)
  const next = (current + direction + items.length) % items.length
  items[next]?.focus()
}

/** WAI-ARIA menu keyboard pattern: arrows, enter/space, escape. */
const handleKeyboard = (
  event: KeyboardEvent,
  panel: HTMLElement,
  closeMenu: () => void
): void => {
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      moveFocus(panel, 1)
      break
    case 'ArrowUp':
      event.preventDefault()
      moveFocus(panel, -1)
      break
    case 'Enter':
    case ' ':
      event.preventDefault()
      ;(document.activeElement as HTMLElement | null)?.click()
      break
    case 'Escape':
      event.preventDefault()
      closeMenu()
      break
  }
}

// ── Module-level singleton ─────────────────────────────────────────────
// Only one context menu can be open at a time. Any instance calls this
// before opening its own panel so the previous one is dismissed first.
let activeCloseFn: (() => void) | null = null

// ── Hook ───────────────────────────────────────────────────────────────

export const FacadeContextMenu: Partial<ContextMenuHook> = {
  panel: null,
  cleanup: null,

  mounted() {
    const trigger = this.el.querySelector(
      '[data-part="context-menu-trigger"]'
    ) as HTMLElement | null
    if (!trigger) {
      return
    }

    const itemsHost = this.el.querySelector(
      '[data-part="context-menu-items"]'
    ) as HTMLElement | null
    if (!itemsHost) {
      return
    }

    // ── Close helper ───────────────────────────────────────────────
    const closeMenu = (): void => {
      if (!this.panel) {
        return
      }
      this.panel.remove()
      this.panel = null
      if (activeCloseFn === closeMenu) {
        activeCloseFn = null
      }
      document.removeEventListener('click', onClickOutside, true)
      document.removeEventListener('keydown', onKeydown, true)
      window.removeEventListener('scroll', closeMenu, true)
    }

    // ── Dismiss listeners ──────────────────────────────────────────
    const onClickOutside = (e: MouseEvent): void => {
      if (this.panel && !this.panel.contains(e.target as Node)) {
        closeMenu()
      }
    }

    const onKeydown = (e: KeyboardEvent): void => {
      if (!this.panel) {
        return
      }
      handleKeyboard(e, this.panel, closeMenu)
    }

    // ── Open on right-click ────────────────────────────────────────
    const onContextMenu = (e: MouseEvent): void => {
      if (this.el.dataset.disabled === 'true') {
        return
      }
      e.preventDefault()

      // Close any existing menu (from this or another instance)
      activeCloseFn?.()
      closeMenu()

      // Clone items from the hidden host (not a <template> — see note above)
      const content = document.createDocumentFragment()
      for (const child of Array.from(itemsHost.childNodes)) {
        content.appendChild(child.cloneNode(true))
      }

      // Build floating panel
      const panel = document.createElement('div')
      panel.setAttribute('role', 'menu')
      panel.setAttribute('data-floating', '')
      panel.setAttribute('data-component', '.context-menu-panel')
      panel.classList.add('context-menu-panel')
      panel.appendChild(content)

      document.body.appendChild(panel)
      this.panel = panel
      activeCloseFn = closeMenu

      positionPanel(panel, e.clientX, e.clientY)

      // Focus the first item
      getFocusableItems(panel)[0]?.focus()

      // ── Item click handler (delegation) ────────────────────────
      panel.addEventListener('click', (ev) => {
        const item = (ev.target as HTMLElement).closest<HTMLElement>(
          '[role="menuitem"]'
        )
        if (!item || item.getAttribute('aria-disabled') === 'true') {
          return
        }

        const confirmMsg = item.dataset.confirm
        if (confirmMsg && !confirm(confirmMsg)) {
          return
        }

        sendItemEvent(item, this as unknown as ViewHook)
        closeMenu()
      })

      // Attach dismiss listeners
      document.addEventListener('click', onClickOutside, true)
      document.addEventListener('keydown', onKeydown, true)
      window.addEventListener('scroll', closeMenu, true)
    }

    trigger.addEventListener('contextmenu', onContextMenu)

    // ── Server-driven show/hide ────────────────────────────────────
    this.handleEvent('context_menu:hide', (payload: { id: string }) => {
      if (payload.id === this.el.id) {
        closeMenu()
      }
    })

    // ── Cleanup ────────────────────────────────────────────────────
    this.cleanup = () => {
      trigger.removeEventListener('contextmenu', onContextMenu)
      closeMenu()
    }
  },

  destroyed() {
    this.cleanup?.()
    this.cleanup = null
  }
}
